import React, { useState, useEffect } from 'react'
import { Form, Button, Container, Row, Col, Alert, InputGroup } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { EyeFill, EyeSlashFill } from 'react-bootstrap-icons'
import { userAPI } from '../api/userAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'

function UserPasswordChangeScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [user, setUser] = useState({ username: '' })
    const [passwordData, setPasswordData] = useState({
        new_password: '',
        confirm_password: ''
    })
    const [showPasswords, setShowPasswords] = useState({
        new: false,
        confirm: false
    })

    useEffect(() => {
        async function fetchUser() {
            try {
                const data = await userAPI.getById(id)
                setUser(data)
            } catch (error) {
                console.error('Error fetching user:', error)
                setError('Failed to load user data')
            }
        }
        fetchUser()
    }, [id])

    const handlePasswordChange = async (e) => {
        e.preventDefault()
        if (passwordData.new_password !== passwordData.confirm_password) {
            setError('New passwords do not match')
            return
        }
        try {
            await userAPI.changePassword(id, passwordData.new_password)
            navigate(-1)
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('users.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to change user passwords.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <h1>Change password for {user.username}</h1>
                    <Form onSubmit={handlePasswordChange}>
                        <Form.Group className="mb-4">
                            <Form.Label>New Password*</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showPasswords.new ? "text" : "password"}
                                    value={passwordData.new_password}
                                    onChange={(e) => setPasswordData({
                                        ...passwordData,
                                        new_password: e.target.value
                                    })}
                                    placeholder="Enter new password"
                                    required
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                <Button 
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowPasswords({
                                        ...showPasswords,
                                        new: !showPasswords.new
                                    })}
                                >
                                    {showPasswords.new ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>Confirm Password*</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showPasswords.confirm ? "text" : "password"}
                                    value={passwordData.confirm_password}
                                    onChange={(e) => setPasswordData({
                                        ...passwordData,
                                        confirm_password: e.target.value
                                    })}
                                    placeholder="Confirm new password"
                                    required
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                <Button 
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowPasswords({
                                        ...showPasswords,
                                        confirm: !showPasswords.confirm
                                    })}
                                >
                                    {showPasswords.confirm ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                        </Form.Group>
                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        <Row className="mt-3">
                            <Col>
                                <Button 
                                    variant="primary" 
                                    type="submit" 
                                    className="me-2"
                                >
                                    Change Password
                                </Button>
                                <Button 
                                    variant="secondary" 
                                    onClick={() => navigate(-1)}
                                >
                                    Cancel
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default UserPasswordChangeScreen 